import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { HiX, HiArrowRight } from 'react-icons/hi';
import data from '../../data/index.json';
import { Section, SectionHeader, reveal } from '../../components';

const initialFormState = {
  name: '',
  email: '',
  organization: '',
  phone: '',
  interestType: 'investor',
  message: ''
};

/** Headline figures, presented as a record rather than a dashboard widget. */
const metrics = [
  { value: '94.8%', label: 'Model accuracy index' },
  { value: '5+',    label: 'Years of R&D' },
  { value: '10+',   label: 'Enterprise clients' },
  { value: '3',     label: 'Countries served' },
];

const InvestorHub = () => {
  const { t } = useTranslation();
  const { companyInfo } = data;
  const partnershipFormUrl = "https://forms.gle/zyHAQbjtcpDLbWGi8";
  const [formData, setFormData] = useState(initialFormState);
  const [submitState, setSubmitState] = useState({ type: 'idle', message: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeInterest, setActiveInterest] = useState('investor');

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const closeInquiryModal = () => {
    setIsModalOpen(false);
    setSubmitState({ type: 'idle', message: '' });
  };

  useEffect(() => {
    if (!isModalOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsModalOpen(false);
        setSubmitState({ type: 'idle', message: '' });
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen]);

  const openInquiryModal = (interestType) => {
    setActiveInterest(interestType);
    setFormData((current) => ({ ...current, interestType }));
    setSubmitState({ type: 'idle', message: '' });
    setIsModalOpen(true);
  };

  // Kept alongside the inquiry modal: both are intentionally unwired, since the
  // trigger buttons are commented out. Deploy runs with CI=true, so the unused
  // warning has to be silenced rather than left to fail the build.
  // eslint-disable-next-line no-unused-vars
  const handlePartnershipClick = () => {
    if (partnershipFormUrl) {
      window.open(partnershipFormUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    openInquiryModal('partner');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const scriptUrl = process.env.REACT_APP_GOOGLE_SCRIPT_URL?.trim();

    if (!scriptUrl) {
      setSubmitState({
        type: 'error',
        message: t('Set REACT_APP_GOOGLE_SCRIPT_URL to enable live submission to your Google Sheet.')
      });
      return;
    }

    setSubmitState({ type: 'loading', message: t('Sending your inquiry...') });

    try {
      const response = await fetch(scriptUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: new URLSearchParams({
          ...formData,
          source: 'Investor / Partnership Inquiry'
        }).toString()
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      setSubmitState({
        type: 'success',
        message: t('Thank you. Your inquiry has been received and we will follow up shortly.')
      });
      setFormData(initialFormState);
    } catch (error) {
      setSubmitState({
        type: 'error',
        message: t('We could not send your inquiry right now. Please email us directly instead.')
      });
    }
  };

  const opportunities = [
    { key: 'investmentOpportunities', label: t('For investors') },
    { key: 'partnershipOpportunities', label: t('For partners') },
  ];

  return (
    <Section id="investor" tone="paper">
      <SectionHeader
        index="04"
        label={t('Strategic Infrastructure')}
        title={
          <>
            {t('Capitalizing on')} <em>{t('growth')}</em>
          </>
        }
        lede={t(
          "Hyphen Tech is architecting the digital backbone of the region. Our performance metrics reflect a commitment to engineering excellence and sustainable scalability."
        )}
        aside={
          <a href="#contact" className="btn btn-solid">
            {t('Start a conversation')}
            <HiArrowRight aria-hidden />
          </a>
        }
      />

      {/* ── Record of performance ── */}
      <dl className="grid grid-cols-2 md:grid-cols-4 mt-14 md:mt-20 border-t border-[color:var(--rule)]">
        {metrics.map((m, i) => (
          <motion.div
            key={m.label}
            {...reveal(i)}
            className={`py-7 md:py-9 border-b border-[color:var(--rule)] md:border-b-0 ${
              i % 2 === 1 ? 'pl-6 border-l' : ''
            } md:border-l md:first:border-l-0 md:px-8 md:first:pl-0 border-[color:var(--rule)]`}
          >
            <dd className="display-2 tabular">{m.value}</dd>
            <dt className="label mt-3">{t(m.label)}</dt>
          </motion.div>
        ))}
      </dl>

      {/* ── Two ways in ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 mt-16 md:mt-24">
        {opportunities.map((item, i) => (
          <motion.div key={item.key} {...reveal(i)} className="rule-gold pt-6">
            <span className="label">{item.label}</span>
            <h3 className="display-2 mt-4">{t(companyInfo[item.key].title)}</h3>
            <p className="prose-body mt-4">{t(companyInfo[item.key].desc)}</p>
          </motion.div>
        ))}
      </div>

      {/* ── Innovation portfolio ── */}
      <div className="mt-16 md:mt-24">
        <div className="flex flex-wrap items-baseline justify-between gap-4 border-t border-[color:var(--rule)] pt-3">
          <span className="label">{t('Core Initiatives')}</span>
          <span className="label">{t('Innovation Portfolio 2024-2025')}</span>
        </div>

        <ul className="mt-8">
          {companyInfo.innovationInitiatives.map((item, idx) => (
            <motion.li
              key={idx}
              {...reveal(idx)}
              className="group border-b border-[color:var(--rule)]"
            >
              <a
                href={item.ventureLink || '#ventures'}
                className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 py-7 md:py-8 transition-colors duration-200 hover:bg-paper-warm md:px-4 md:-mx-4"
              >
                <span className="label tabular text-gold-ink md:col-span-1 md:pt-1.5">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <h4 className="display-3 md:col-span-4">{t(item.title)}</h4>
                <p className="md:col-span-6 text-[0.9375rem] leading-relaxed text-ink-soft">
                  {t(item.desc)}
                </p>
                <span className="md:col-span-1 flex md:justify-end md:pt-1.5">
                  <HiArrowRight
                    aria-hidden
                    className="text-lg text-ink-faint transition-all duration-200 group-hover:text-ink group-hover:translate-x-1"
                  />
                </span>
              </a>
            </motion.li>
          ))}
        </ul>
      </div>

      {isModalOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-ink/60 px-4 py-6 backdrop-blur-sm"
              onClick={closeInquiryModal}
              role="presentation"
            >
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="inquiry-title"
                className="relative w-full max-w-[34rem] max-h-[90vh] overflow-y-auto bg-paper border border-[color:var(--rule-strong)] p-6 md:p-8 shadow-xl"
              >
                <button
                  type="button"
                  onClick={closeInquiryModal}
                  className="absolute right-4 top-4 p-2 text-ink-faint transition hover:text-ink"
                  aria-label={t('Close inquiry form')}
                >
                  <HiX className="text-xl" />
                </button>

                <form onSubmit={handleSubmit}>
                  <span className="label">{t('Investor & Partnership Inquiry')}</span>
                  <h3 id="inquiry-title" className="display-2 mt-3 pr-8">
                    {activeInterest === 'partner'
                      ? t('Let us explore a partnership')
                      : t('Let us explore an investment conversation')}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-soft">
                    {t('Share your background and your collaboration idea. We will follow up with the right team member.')}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-7">
                    <label className="block">
                      <span className="field-label">{t('Full name')}</span>
                      <input
                        type="text"
                        name="name"
                        autoComplete="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="field"
                      />
                    </label>
                    <label className="block">
                      <span className="field-label">{t('Email address')}</span>
                      <input
                        type="email"
                        name="email"
                        autoComplete="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="field"
                      />
                    </label>
                    <label className="block">
                      <span className="field-label">{t('Organization')}</span>
                      <input
                        type="text"
                        name="organization"
                        autoComplete="organization"
                        value={formData.organization}
                        onChange={handleInputChange}
                        className="field"
                      />
                    </label>
                    <label className="block">
                      <span className="field-label">{t('Phone number')}</span>
                      <input
                        type="tel"
                        name="phone"
                        autoComplete="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="field"
                      />
                    </label>
                    <label className="block md:col-span-2">
                      <span className="field-label">{t('Type of interest')}</span>
                      <select
                        name="interestType"
                        value={formData.interestType}
                        onChange={handleInputChange}
                        className="field"
                      >
                        <option value="investor">{t('Investor')}</option>
                        <option value="partner">{t('Partner')}</option>
                        <option value="both">{t('Both')}</option>
                      </select>
                    </label>
                    <label className="block md:col-span-2">
                      <span className="field-label">{t('How would you like to collaborate?')}</span>
                      <textarea
                        name="message"
                        rows="5"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        className="field"
                      />
                    </label>
                  </div>

                  <div className="mt-7 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-wrap gap-3">
                      <button
                        type="submit"
                        disabled={submitState.type === 'loading'}
                        className="btn btn-solid disabled:opacity-50"
                      >
                        {submitState.type === 'loading' ? t('Sending...') : t('Submit inquiry')}
                      </button>
                      <button
                        type="button"
                        onClick={closeInquiryModal}
                        className="btn btn-outline"
                      >
                        {t('Cancel')}
                      </button>
                    </div>
                    {submitState.message ? (
                      <p
                        role="status"
                        aria-live="polite"
                        className={`text-sm ${
                          submitState.type === 'error' ? 'text-[color:var(--error-color)]' : 'text-[color:var(--success-color)]'
                        }`}
                      >
                        {submitState.message}
                      </p>
                    ) : null}
                  </div>
                </form>
              </motion.div>
            </div>,
            document.body
          )
        : null}
    </Section>
  );
};

export default InvestorHub;
